import * as routerActions from 'connected-react-router'
import { stringify } from 'querystringify'

// Actions
import { actions as notificationsActions } from 'modules/notifications/actions'
import { actions as payActions } from 'modules/pay/actions'

// Schemas
import {
  beatListSchema,
  beatSchema,
  proListSchema,
  registrationServiceListSchema,
  revenueReportPeriodListSchema,
  userSongListSchema,
  userSongRevenueReportListSchema,
  userSongRevenueReportSchema,
  userSongSchema,
} from 'modules/usersongs/models'

// Types
import { Beat, Dispatch, APIAction } from 'types'

// Utils
import { getAuthToken } from 'modules/auth/actions'
import { getHeader } from 'middleware'

export const actions = {
  activateUserSongLicense: (userSongId: number): APIAction => ({
    CALL_API: {
      type: 'ACTIVATE_USER_SONG',
      method: 'POST',
      endpoint: `/api/usersong/${userSongId}/activate/`,
      schema: userSongSchema,
    },
  }),

  fetchSpecificUserSong: (userSongId: number): APIAction => ({
    CALL_API: {
      type: 'FETCH_SPECIFIC_USER_SONG',
      method: 'GET',
      endpoint: `/api/usersong/${userSongId}/`,
      schema: userSongSchema,
      requestKey: userSongId,
    },
  }),

  fetchAllUserSongs: (): APIAction => ({
    CALL_API: {
      type: 'FETCH_ALL_USER_SONGS',
      method: 'GET',
      endpoint: '/api/usersong/',
      schema: userSongListSchema,
    },
  }),

  createUserSong: (userSongData: Record<string, unknown>): APIAction => ({
    CALL_API: {
      type: 'CREATE_USER_SONG',
      method: 'POST',
      endpoint: '/api/usersong/',
      schema: userSongSchema,
      data: userSongData,
    },
  }),

  deleteUserSong: (userSongId: number) => (dispatch: Dispatch) => {
    dispatch(routerActions.push('/yoursongs/'))
    dispatch(notificationsActions.createUserSongDeletedNotification(userSongId))
    return dispatch(actions._deleteUserSong(userSongId))
  },

  _deleteUserSong: (userSongId: number): APIAction => ({
    CALL_API: {
      type: 'DELETE_USER_SONG',
      method: 'DELETE',
      endpoint: `/api/usersong/${userSongId}/`,
      actionParams: {
        userSongId,
      },
    },
  }),

  updateUserSong: (userSongId: number, userSongUpdate: Record<string, unknown>): APIAction => ({
    CALL_API: {
      type: 'UPDATE_USER_SONG',
      method: 'PATCH',
      endpoint: `/api/usersong/${userSongId}/`,
      requestKey: userSongId.toString(),
      schema: userSongSchema,
      data: userSongUpdate,
    },
  }),

  fetchBeat: (beatId: number): APIAction => ({
    CALL_API: {
      type: 'FETCH_BEAT',
      method: 'GET',
      endpoint: `/api/usersong/beat/${beatId}/`,
      requestKey: beatId.toString(),
      schema: beatSchema,
    },
  }),

  fetchBeatByUser: (userId: number): APIAction => ({
    CALL_API: {
      type: 'FETCH_BEAT_BY_USER',
      method: 'GET',
      endpoint: `/api/usersong/beat/?user_id=${userId}`,
      requestKey: userId.toString(),
      schema: beatListSchema,
    },
  }),

  updateBeat: (beatId: number, beatUpdate: Partial<Beat>): APIAction => {
    return {
      CALL_API: {
        type: 'UPDATE_BEAT',
        method: 'PATCH',
        endpoint: `/api/usersong/beat/${beatId}/`,
        requestKey: beatId.toString(),
        schema: beatSchema,
        data: beatUpdate,
      },
    }
  },

  uploadBeatAudioFile: (audioFile: string, beatId: number) => async (dispatch: Dispatch) => {
    dispatch({ type: 'UPLOAD_BEAT_AUDIO_FILE_START' })
    const endpoint = `/api/usersong/beat/${beatId}/`
    const formData = new FormData()
    formData.append('audio_file', audioFile)
    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: { ...getHeader(getAuthToken() || '', endpoint) },
      body: formData,
    })

    if (response.status === 200) {
      return dispatch({ type: 'UPLOAD_BEAT_AUDIO_FILE_DONE' })
    }
    return dispatch({ type: 'UPLOAD_BEAT_AUDIO_FILE_ERROR' })
  },

  fetchRevenueReports: (userSongId?: number, open?: boolean): APIAction => {
    const query = {
      ...(userSongId ? { user_song_id: userSongId } : {}),
      ...(open ? { open } : {}),
    }
    const queryString = stringify(query)
    return {
      CALL_API: {
        type: 'FETCH_REVENUE_REPORTS',
        method: 'GET',
        endpoint: `/api/usersong/revenuereport/${queryString ? `?${queryString}` : ''}`,
        schema: userSongRevenueReportListSchema,
        requestKey: queryString,
      },
    }
  },

  fetchRevenueReport: (revenueReportId: number): APIAction => ({
    CALL_API: {
      type: 'FETCH_REVENUE_REPORT',
      method: 'GET',
      endpoint: `/api/usersong/revenuereport/${revenueReportId}/`,
      schema: userSongRevenueReportSchema,
      requestKey: revenueReportId.toString(),
    },
  }),

  updateRevenueReport: (revenueReportId: number, revenueReportUpdate: Record<string, unknown>) => async (dispatch: Dispatch) => {
    await dispatch(actions._updateRevenueReport(revenueReportId, revenueReportUpdate))
    return dispatch(payActions.fetchLatestRevenueOrder())
  },

  _updateRevenueReport: (revenueReportId: number, revenueReportUpdate: Record<string, unknown>): APIAction => ({
    CALL_API: {
      type: 'UPDATE_REVENUE_REPORT',
      method: 'PATCH',
      endpoint: `/api/usersong/revenuereport/${revenueReportId}/`,
      schema: userSongRevenueReportSchema,
      data: revenueReportUpdate,
    },
  }),

  fetchRegistrationServices: (): APIAction => ({
    CALL_API: {
      type: 'FETCH_REGISTRATION_SERVICES',
      method: 'GET',
      endpoint: '/api/usersong/registrationservice',
      schema: registrationServiceListSchema,
    },
  }),

  uploadAudioFile: (audioFile: string, userSongId: number) => async (dispatch: Dispatch) => {
    dispatch({ type: 'UPLOAD_AUDIO_FILE_START' })
    const endpoint = `/api/usersong/${userSongId}/`
    const formData = new FormData()
    formData.append('audio_file', audioFile)
    const response = await fetch(endpoint, {
      method: 'PATCH',
      headers: { ...getHeader(getAuthToken() || '', endpoint) },
      body: formData,
    })

    if (response.status === 200) {
      return dispatch({ type: 'UPLOAD_AUDIO_FILE_DONE' })
    }
    return dispatch({ type: 'UPLOAD_AUDIO_FILE_ERROR' })
  },

  sendUserSong: (userSongId: number, email: string, message?: string): APIAction => ({
    CALL_API: {
      type: 'SEND_USER_SONG',
      method: 'POST',
      endpoint: `/api/usersong/${userSongId}/send/`,
      requestKey: userSongId,
      data: {
        email,
        message,
      },
    },
  }),

  receiveUserSong: (sendToken: string): APIAction => ({
    CALL_API: {
      type: 'RECEIVE_USER_SONG',
      method: 'POST',
      endpoint: `/api/usersong/receive/${sendToken}/`,
      schema: userSongSchema,
    },
  }),

  fetchSentUserSongInfo: (sendToken: string): APIAction => ({
    CALL_API: {
      type: 'FETCH_SENT_USER_SONG_INFO',
      method: 'GET',
      endpoint: `/api/usersong/receive/${sendToken}/`,
    },
  }),

  fetchPROs: (): APIAction => ({
    CALL_API: {
      type: 'FETCH_PROS',
      method: 'GET',
      endpoint: '/api/pros/',
      schema: proListSchema,
    },
  }),

  fetchReportingPeriods: (): APIAction => ({
    CALL_API: {
      type: 'FETCH_REPORTING_PERIODS',
      method: 'GET',
      endpoint: '/api/usersong/periods/',
      schema: revenueReportPeriodListSchema,
    },
  }),
}
